import { BigNumber } from '@0x/utils'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import { ChainId, stakingConfig } from '../config/appConfig'
import {
  BorrowEvent,
  BurnEvent,
  CloseWithDepositEvent,
  CloseWithSwapEvent,
  DepositCollateralEvent,
  EarnRewardEventNew,
  LiquidationEvent,
  MintEvent,
  PayTradingFeeEvent,
  RolloverEvent,
  StakeEvent,
  UnstakeEvent,
  TradeEvent,
  WithdrawCollateralEvent,
} from '../domain/events'
import bzxApi from './apis/bzxApi'
import { getTimestamp } from './dateUtils'

type BaseEvent = {
  chainId: ChainId
  id: string // "0x000273c35edbc480ad2e1543dafdc9b69eccc0b315da62f6d72103f7e5bb7716-138",
  loan: {
    id: string // "0x980a73c752722d83417470483f9badc176ea723b7535cfecbff5b734aeca890d",
    collateralToken: string // "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
    loanToken: string // "0xdac17f958d2ee523a2206206994597c13d831ec7"
  }
  address: string // "0xd8ee69652e4e4838f2531732a46d1f7f584f0b7f",
  timestamp: number // 1606820164,
  transaction: {
    id: string // "0x000273c35edbc480ad2e1543dafdc9b69eccc0b315da62f6d72103f7e5bb7716"
    blockNumber: string // "11582776"
    timestamp: string // 1609693710
  }
  type: string // "ProtocolTradeEvent",
  user: {
    id: string // "0xd22ac5af1c0db9a22a65a94db00a72b4e6b08ee2"
  }
}

type SubgraphTradeEvent = BaseEvent & {
  borrowedAmount: string // "64987760",
  currentLeverage: string // "4167269003888986642",
  entryLeverage: string // "4000000000000000000",
  entryPrice: string // "1589635849229932136121008833",
  interestRate: string // "19000000000000000000",
  lender: {
    id: string // "0x7e9997a38a439b2be7ed9c9c4628391d3e055d48"
  }
  positionSize: string // "129414799655130232",
  settlementDate: number // 1609239364,
}

type SubgraphBorrowEvent = BaseEvent & {
  newCollateral: string // "64987760",
  currentMargin: string // "4167269003888986642",
  collateralToLoanRate: string // "4000000000000000000",
  interestRate: string // "19000000000000000000",
  lender: {
    id: string // "0x7e9997a38a439b2be7ed9c9c4628391d3e055d48"
  }
  newPrincipal: string // "129414799655130232",
  interestDuration: number // 1609239364,
}

type SubgraphLiquidateEvent = BaseEvent & {
  collateralToLoanRate: string // '31935220394913006423'
  collateralWithdrawAmount: string // '75374874335927337991'
  currentMargin: string // '13974655882350922435'
  liquidator: {
    id: string // '0xfa084336deba96eb0519120d2faa446e15c568ca'
  }
  repayAmount: string // '2249638527249263248153'
}

type SubgraphRolloverEvent = BaseEvent & {
  caller: {
    id: string // "0xe487a866b0f6b1b663b4566ff7e998af6116fba9"
  }
  collateralAmountUsed: string // "143847530916363",
  gasRebate: string // "6663500000000000",
  interestAmountAdded: string // "95812595075377483",
  loanEndTimestamp: number // 1623174271,
}

type SubgraphCloseWithDepositEvent = BaseEvent & {
  closer: {
    id: string // '0xe33dc6024fb048d894deea298e5468bbb2108b7b'
  }
  repayAmount: string // '0443'
  collateralWithdrawAmount: string // '28967644427526'
  collateralToLoanRate: string // '970439278783124555505'
  currentMargin: string // '36305878781841533'
}

type SubgraphCloseWithSwapEvent = BaseEvent & {
  closer: {
    id: string // '0xe33dc6024fb048d894deea298e5468bbb2108b7b'
  }
  currentLeverage: string // '0'
  exitPrice: string // '28967644427526'
  loanCloseAmount: string // '970439278783124555505'
  positionCloseSize: string // '36305878781841533'
}

type SubgraphEarnRewardEvent = BaseEvent & {
  amount: string // '19369838282748679012'
  feeType: number // 3
  receiver: {
    id: string // '0xbfb3c2cf90b17cabf40e73384e1fea5d64d83644'
  }
  token: string // '0xb72b31907c1c95f3650b64b2469e08edacee5e8f'
}

type SubgraphPayTradingFeeEvent = BaseEvent & {
  amount: string // '74031735924876064807'
  payer: {
    id: string // '0x6c10bfcd97611c671a23752e8e48bf5af26049c3'
  }
  token: string // '0x6b175474e89094c44da98b954eedeac495271d0f'
}

type SubgraphDepositCollateralEvent = BaseEvent & {
  depositAmount: string // '5928881552879280376'
  depositToken: string // '0x514910771af9ca656af840dff83e8264ecf986ca'
}

type SubgraphWithdrawCollateralEvent = BaseEvent & {
  withdrawAmount: string // '5928881552879280376'
  withdrawToken: string // '0x514910771af9ca656af840dff83e8264ecf986ca'
  timestamp: number // 1609291944
}

type SubgraphMintEvent = BaseEvent & {
  price: string // '53339'
  transaction: {
    blockNumber: string // '11552379'
    id: string // '0x002ca365e3ca09ead22611de1b7d74a185f50cc66a10725f08a0cbc90aa640ef'
    timestamp: number // 1609291944
  }
  minter: {
    id: string // '0x23387f54e216c87f7c27a73bdb12bc4ca9b818eb'
  }
  tokenAmount: string
  assetAmount: string
}

type SubgraphBurnEvent = BaseEvent & {
  address: string // '0xd8ee69652e4e4838f2531732a46d1f7f584f0b7f'
  price: string // '53339'
  burner: {
    id: string // '0x23387f54e216c87f7c27a73bdb12bc4ca9b818eb'
  }
  tokenAmount: string
  assetAmount: string
}

type SubgraphStakeEvent = BaseEvent & {
  amount: string
  token: string
}

type SubgraphUnstakeEvent = BaseEvent & {
  amount: string
  token: string
}

export type ActivityEvent =
  | TradeEvent
  | LiquidationEvent
  | CloseWithSwapEvent
  | CloseWithDepositEvent
  | DepositCollateralEvent
  | WithdrawCollateralEvent
  | BorrowEvent
  | MintEvent
  | BurnEvent
  | StakeEvent
  | UnstakeEvent

type TradeHistoryEvent =
  | TradeEvent
  | LiquidationEvent
  | RolloverEvent
  | CloseWithSwapEvent
  | DepositCollateralEvent
  | WithdrawCollateralEvent
  | EarnRewardEventNew
  | PayTradingFeeEvent

const activityEvents = [
  'protocolTradeEvent',
  'protocolLiquidateEvent',
  'protocolRolloverEvent',
  'protocolCloseWithSwapEvent',
  'protocolDepositCollateralEvent',
  'protocolWithdrawCollateralEvent',
  'protocolEarnRewardEvent',
  'protocolPayTradingFeeEvent',
  'protocolCloseWithDepositEvent',
  'protocolBorrowEvent',
  'mintEvent',
  'burnEvent',
  'stakingStakeEvent',
  'stakingUnstakeEvent',
]

const tradeHistoryEvents = [
  'protocolTradeEvent',
  'protocolLiquidateEvent',
  'protocolRolloverEvent',
  'protocolCloseWithSwapEvent',
  'protocolDepositCollateralEvent',
  'protocolWithdrawCollateralEvent',
  'protocolEarnRewardEvent',
  'protocolPayTradingFeeEvent',
]

type ProtocolEvent = typeof activityEvents[number] | typeof tradeHistoryEvents[number]

const params = `orderBy: timestamp, orderDirection: desc`

const timeFilter = (monthCount = 0) => `timestamp_gt: ${monthCount ? getTimestamp(monthCount) : 0}`

const baseEventQuery = `id
user {
  id
}
address
type
transaction {
  id
  blockNumber
  timestamp
}
timestamp
loan {
  id
  loanToken
  collateralToken
}`

const eventFields: Record<ProtocolEvent, string> = {
  protocolTradeEvent: `lender {
      id
    }
    positionSize
    borrowedAmount
    interestRate
    settlementDate
    entryPrice
    entryLeverage
    currentLeverage`,
  protocolCloseWithSwapEvent: `closer{
      id
    }   
    positionCloseSize
    loanCloseAmount
    exitPrice
    currentLeverage`,
  protocolCloseWithDepositEvent: `closer{
      id
    }   
    repayAmount
    collateralWithdrawAmount
    collateralToLoanRate
    currentMargin`,
  protocolBorrowEvent: `lender{
      id
     }
     newPrincipal
     newCollateral
     interestRate
     interestDuration
     collateralToLoanRate
     currentMargin`,
  mintEvent: `minter{
       id
    }
     tokenAmount
    assetAmount
    price`,
  burnEvent: `burner{
     id
    }
    tokenAmount
    assetAmount
    price`,
  protocolRolloverEvent: `caller{
      id
    }
    collateralAmountUsed
    interestAmountAdded
    loanEndTimestamp
    gasRebate`,
  protocolLiquidateEvent: `liquidator{
      id
    }
    repayAmount
    collateralWithdrawAmount
    collateralToLoanRate
    currentMargin`,
  protocolDepositCollateralEvent: `depositToken        
    depositAmount`,
  protocolWithdrawCollateralEvent: `withdrawToken
    withdrawAmount`,
  protocolEarnRewardEvent: `receiver{
      id
    }
    token
    amount
    feeType`,
  protocolPayTradingFeeEvent: `payer{
      id
    }
    token
    amount`,
  stakingStakeEvent: `token
    amount`,
  stakingUnstakeEvent: `token
    amount`,
}

function getEventsQuery(
  type: ProtocolEvent,
  account?: string,
  opts?: { oldGraph: boolean; monthCount?: number }
) {
  const fields = eventFields[type]
  const accountFilter = account ? `user: "${account.toLowerCase()}"` : ''
  return `${type}s${`(where: {${timeFilter(opts?.monthCount)},${accountFilter}}, ${params})`}{
     ${baseEventQuery}
     ${fields}
  }`
}

function getLoanEventsQuery(type: ProtocolEvent, loanIds: string[]) {
  const loans = loanIds.reduce((ids, id) => `${ids}"${id.toLowerCase()}",`, '')
  const loanFilter = loanIds.length ? `loan_: {id_in: [${loans}]}` : ''
  return `${type}s${`(where: {${loanFilter}}, ${params})`}{
     ${baseEventQuery}`
}

const getTradeEvents = (events: SubgraphTradeEvent[], chainId: ChainId): TradeEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new TradeEvent(
        chainId,
        e.user.id,
        e.lender.id,
        e.loan.id,
        collateralToken,
        loanToken,
        new BigNumber(e.positionSize),
        new BigNumber(e.borrowedAmount),
        new BigNumber(e.interestRate),
        new Date(new BigNumber(e.settlementDate).times(1000).toNumber()),
        new BigNumber(e.entryPrice),
        new BigNumber(e.entryLeverage),
        new BigNumber(e.currentLeverage),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as TradeEvent[])
}

const getLiquidationEvents = (
  events: SubgraphLiquidateEvent[],
  chainId: ChainId
): LiquidationEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new LiquidationEvent(
        chainId,
        e.user.id,
        e.liquidator.id,
        e.loan.id,
        e.address,
        loanToken,
        collateralToken,
        new BigNumber(e.repayAmount),
        new BigNumber(e.collateralWithdrawAmount),
        new BigNumber(e.collateralToLoanRate),
        new BigNumber(e.currentMargin),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as LiquidationEvent[])
}

const getRolloverEvents = (events: SubgraphRolloverEvent[], chainId: ChainId): RolloverEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new RolloverEvent(
        e.user.id,
        e.caller.id,
        e.loan.id,
        e.address,
        loanToken,
        collateralToken,
        new BigNumber(e.collateralAmountUsed),
        new BigNumber(e.interestAmountAdded),
        new Date(new BigNumber(e.loanEndTimestamp).times(1000).toNumber()),
        new BigNumber(e.gasRebate),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as RolloverEvent[])
}

const getCloseWithSwapEvents = (
  events: SubgraphCloseWithSwapEvent[],
  chainId: ChainId
): CloseWithSwapEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new CloseWithSwapEvent(
        chainId,
        e.user.id,
        collateralToken,
        loanToken,
        e.address,
        e.closer.id,
        e.loan.id,
        new BigNumber(e.positionCloseSize),
        new BigNumber(e.loanCloseAmount),
        new BigNumber(e.exitPrice),
        new BigNumber(e.currentLeverage),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as CloseWithSwapEvent[])
}

const getCloseWithDepositEvents = (
  events: SubgraphCloseWithDepositEvent[],
  chainId: ChainId
): CloseWithDepositEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new CloseWithDepositEvent(
        chainId,
        e.user.id,
        e.address,
        e.loan.id,
        e.closer.id,
        loanToken,
        collateralToken,
        new BigNumber(e.repayAmount),
        new BigNumber(e.collateralWithdrawAmount),
        new BigNumber(e.collateralToLoanRate),
        new BigNumber(e.currentMargin),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as CloseWithDepositEvent[])
}

const getEarnRewardEvents = (
  events: SubgraphEarnRewardEvent[],
  chainId: ChainId
): EarnRewardEventNew[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(chainId, e.token)
    if (token === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new EarnRewardEventNew(
        e.receiver.id,
        e.loan.id,
        e.feeType,
        token,
        new BigNumber(e.amount).div(10 ** 18),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as EarnRewardEventNew[])
}

const getPayTradingFeeEvents = (
  events: SubgraphPayTradingFeeEvent[],
  chainId: ChainId
): PayTradingFeeEvent[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(chainId, e.token)
    if (token === Asset.UNKNOWN) {
      return evnts
    }

    const decimals = assetUtils.getDecimals(chainId, token)
    evnts.push(
      new PayTradingFeeEvent(
        e.user.id,
        token,
        e.loan.id,
        new BigNumber(e.amount).div(10 ** decimals),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as PayTradingFeeEvent[])
}

const getDepositCollateralEvents = (
  events: SubgraphDepositCollateralEvent[],
  chainId: ChainId
): DepositCollateralEvent[] => {
  return events.reduce((evnts, e) => {
    const depositToken = assetUtils.getAssetFromAddress(chainId, e.depositToken)

    if (depositToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new DepositCollateralEvent(
        chainId,
        e.user.id,
        depositToken,
        e.loan.id,
        new BigNumber(e.depositAmount),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as DepositCollateralEvent[])
}

const getWithdrawCollateralEvents = (
  events: SubgraphWithdrawCollateralEvent[],
  chainId: ChainId
): WithdrawCollateralEvent[] => {
  return events.reduce((evnts, e) => {
    const withdrawToken = assetUtils.getAssetFromAddress(chainId, e.withdrawToken)

    if (withdrawToken === Asset.UNKNOWN) {
      return evnts
    }

    evnts.push(
      new WithdrawCollateralEvent(
        chainId,
        e.user.id,
        withdrawToken,
        e.loan.id,
        new BigNumber(e.withdrawAmount),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as WithdrawCollateralEvent[])
}

const getBorrowEvents = (events: SubgraphBorrowEvent[], chainId: ChainId): BorrowEvent[] => {
  return events.reduce((evnts, e) => {
    const loanToken = assetUtils.getAssetFromAddress(chainId, e.loan.loanToken)
    const collateralToken = assetUtils.getAssetFromAddress(chainId, e.loan.collateralToken)
    if (loanToken === Asset.UNKNOWN || collateralToken === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new BorrowEvent(
        chainId,
        e.user.id,
        e.lender.id,
        e.loan.id,
        loanToken,
        collateralToken,
        new BigNumber(e.newPrincipal),
        new BigNumber(e.newCollateral),
        new BigNumber(e.interestRate),
        new BigNumber(e.interestDuration),
        new BigNumber(e.collateralToLoanRate),
        new BigNumber(e.currentMargin),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as BorrowEvent[])
}

const getMintEvents = (events: SubgraphMintEvent[], chainId: ChainId): MintEvent[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(chainId, e.address)

    if (token === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new MintEvent(
        chainId,
        e.minter.id,
        new BigNumber(e.tokenAmount),
        new BigNumber(e.assetAmount),
        new BigNumber(e.price),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        token,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as MintEvent[])
}

const getBurnEvents = (events: SubgraphBurnEvent[], chainId: ChainId): BurnEvent[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(chainId, e.address)

    if (token === Asset.UNKNOWN) {
      return evnts
    }
    evnts.push(
      new BurnEvent(
        chainId,
        e.burner.id,
        new BigNumber(e.tokenAmount),
        new BigNumber(e.assetAmount),
        new BigNumber(e.price),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        token,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as BurnEvent[])
}

const getStakeEvents = (events: SubgraphStakeEvent[]): StakeEvent[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(1, e.token)

    if (!stakingConfig.stakeableAssets.includes(token)) {
      return evnts
    }

    evnts.push(
      new StakeEvent(
        e.user.id,
        token,
        new BigNumber(e.amount),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as StakeEvent[])
}

const getUnstakeEvents = (events: SubgraphUnstakeEvent[]): UnstakeEvent[] => {
  return events.reduce((evnts, e) => {
    const token = assetUtils.getAssetFromAddress(1, e.token)

    if (!stakingConfig.stakeableAssets.includes(token)) {
      return evnts
    }

    evnts.push(
      new UnstakeEvent(
        e.user.id,
        token,
        new BigNumber(e.amount),
        new BigNumber(e.transaction.blockNumber),
        e.transaction.id,
        new Date(new BigNumber(e.transaction.timestamp).times(1000).toNumber())
      )
    )
    return evnts
  }, [] as UnstakeEvent[])
}

function getActivityEvents(
  events:
    | SubgraphTradeEvent[]
    | SubgraphCloseWithSwapEvent[]
    | SubgraphLiquidateEvent[]
    | SubgraphDepositCollateralEvent[]
    | SubgraphWithdrawCollateralEvent[]
    | SubgraphCloseWithDepositEvent[]
    | SubgraphBorrowEvent[]
    | SubgraphMintEvent[]
    | SubgraphBurnEvent[]
    | SubgraphStakeEvent[]
    | SubgraphUnstakeEvent[],
  chainId: ChainId,
  type: ProtocolEvent
) {
  switch (type) {
    case 'protocolTradeEvent':
      return getTradeEvents(events as SubgraphTradeEvent[], chainId)
    case 'protocolCloseWithSwapEvent':
      return getCloseWithSwapEvents(events as SubgraphCloseWithSwapEvent[], chainId)
    case 'protocolLiquidateEvent':
      return getLiquidationEvents(events as SubgraphLiquidateEvent[], chainId)
    case 'protocolDepositCollateralEvent':
      return getDepositCollateralEvents(events as SubgraphDepositCollateralEvent[], chainId)
    case 'protocolWithdrawCollateralEvent':
      return getWithdrawCollateralEvents(events as SubgraphWithdrawCollateralEvent[], chainId)
    case 'protocolCloseWithDepositEvent':
      return getCloseWithDepositEvents(events as SubgraphCloseWithDepositEvent[], chainId)
    case 'protocolBorrowEvent':
      return getBorrowEvents(events as SubgraphBorrowEvent[], chainId)
    case 'mintEvent':
      return getMintEvents(events as SubgraphMintEvent[], chainId)
    case 'burnEvent':
      return getBurnEvents(events as SubgraphBurnEvent[], chainId)
    case 'stakingStakeEvent':
      return getStakeEvents(events as SubgraphStakeEvent[])
    case 'stakingUnstakeEvent':
      return getUnstakeEvents(events as SubgraphUnstakeEvent[])
  }

  return []
}

function getTradeHistoryEvents(
  events:
    | SubgraphTradeEvent[]
    | SubgraphCloseWithSwapEvent[]
    | SubgraphCloseWithDepositEvent[]
    | SubgraphRolloverEvent[]
    | SubgraphLiquidateEvent[]
    | SubgraphDepositCollateralEvent[]
    | SubgraphWithdrawCollateralEvent[]
    | SubgraphEarnRewardEvent[]
    | SubgraphPayTradingFeeEvent[],
  chainId: ChainId,
  type: ProtocolEvent
) {
  switch (type) {
    case 'protocolTradeEvent':
      return getTradeEvents(events as SubgraphTradeEvent[], chainId)
    case 'protocolCloseWithSwapEvent':
      return getCloseWithSwapEvents(events as SubgraphCloseWithSwapEvent[], chainId)
    case 'protocolRolloverEvent':
      return getRolloverEvents(events as SubgraphRolloverEvent[], chainId)
    case 'protocolLiquidateEvent':
      return getLiquidationEvents(events as SubgraphLiquidateEvent[], chainId)
    case 'protocolDepositCollateralEvent':
      return getDepositCollateralEvents(events as SubgraphDepositCollateralEvent[], chainId)
    case 'protocolWithdrawCollateralEvent':
      return getWithdrawCollateralEvents(events as SubgraphWithdrawCollateralEvent[], chainId)
    case 'protocolEarnRewardEvent':
      return getEarnRewardEvents(events as SubgraphEarnRewardEvent[], chainId)
    case 'protocolPayTradingFeeEvent':
      return getPayTradingFeeEvents(events as SubgraphPayTradingFeeEvent[], chainId)
  }

  return []
}

async function getChainHistory(
  chainId: ChainId,
  account?: string,
  opts?: { oldGraph: boolean; monthCount?: number }
) {
  const history = activityEvents
  const query = `{ ${history.reduce((result, type) => {
    result = `${result && `${result}, `}${getEventsQuery(type, account, opts)}`

    return result
  }, ``)}
}`
  const resp = await bzxApi.subgraphFetch(query, chainId, opts)
  if (!resp) {
    throw new Error('subgraph request failed')
  }

  return history.reduce((result, type) => {
    const events: Array<ActivityEvent> = getActivityEvents(
      resp.data.data[`${type}s`],
      chainId,
      type
    )
    if (!events) {
      return result
    }
    result = events.concat(result)
    return result
  }, [] as ActivityEvent[])
}

async function getTradeHistory(
  chainId: ChainId,
  account?: string,
  opts?: { oldGraph: boolean; allNetworks: boolean; monthCount?: number }
) {
  const query = `
  { ${tradeHistoryEvents.reduce((result, type) => {
    result = `${result && `${result}, `}${getEventsQuery(type, account, opts)}`
    return result
  }, ``)}
}`

  const resp = await bzxApi.subgraphFetch(query, chainId, opts)
  if (!resp) {
    throw new Error('subgraph request failed')
  }

  return tradeHistoryEvents.reduce((result, type) => {
    const events: Array<TradeHistoryEvent> = getTradeHistoryEvents(
      resp.data.data[`${type}s`],
      chainId,
      type
    )
    if (!events) {
      return result
    }
    result = events.concat(result)
    return result
  }, [] as TradeHistoryEvent[])
}

async function getTradeLoans(chainId: ChainId, loanIds: string[]) {
  const query = `
{ ${getLoanEventsQuery('protocolTradeEvent', loanIds)}}
}`
  const resp = await bzxApi.subgraphFetch(query, chainId)

  if (!resp) {
    throw new Error('subgraph request failed')
  }

  return resp.data.data['protocolTradeEvents'].map((event: BaseEvent) => event.loan.id)
}

export default {
  getTradeLoans,
  getChainHistory,
  getTradeHistory,
}
